import { valibotResolver } from "@hookform/resolvers/valibot";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import type { OutreachSourceType } from "features/integrations/types";

import Loader from "common/components/Loader";
import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerClose,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useCrmLeads from "../../../datahooks/useCrmLeads";
import { CrmSourceSchema, type CrmSourceSchemaType } from "../../../schemas";
import CsvOverview from "../CsvOverview";
import SourceFormField from "./SourceFormField";
import SourceTypeFormField from "./SourceTypeFormField";

import type { CrmLeadListSource } from "../../../types";

import CrmImage from "assets/images/sources/crm.png";

interface ConnectCrmProps {
  createSource: (params: {
    settings: CrmLeadListSource["settings"];
    crm_type: OutreachSourceType;
  }) => void;
  isCreatingSource: boolean;
  goBack: () => void;
}

export default function ConnectCrm({
  createSource,
  isCreatingSource,
  goBack,
}: ConnectCrmProps) {
  const isDesktop = useTwBreakpoint("lg");

  const {
    watch,
    setValue,
    register,
    getValues,
    handleSubmit,
    formState: { errors },
    control,
    resetField,
  } = useForm<CrmSourceSchemaType>({
    resolver: valibotResolver(CrmSourceSchema),
  });

  const sourceType = watch("sourceType");
  const sourceId = watch("sourceId");
  const mainIdentifier = watch("mainIdentifier");

  const [isNextStep, setIsNextStep] = useState<boolean>(false);

  const { crmLeadsState, isLoadingCrmLeadsState } = useCrmLeads(
    sourceType,
    sourceId,
    mainIdentifier,
  );

  useEffect(() => {
    if (crmLeadsState) {
      const { leads, state } = crmLeadsState;

      if (state === "success" && leads.url_columns.length) {
        setValue("mainIdentifier", leads.url_columns[0]);
      }
    }
  }, [crmLeadsState, setValue]);

  useEffect(() => {
    resetField("mainIdentifier");
    resetField("sourceId");
  }, [sourceType]);

  useEffect(() => {
    setValue("mainIdentifier", "");
  }, [sourceId, setValue]);

  const onNext = handleSubmit(() => setIsNextStep(true));
  function onSave() {
    createSource({
      settings: {
        type: "crm",
        profiles: crmLeadsState.leads.parsed,
        url_column: getValues("mainIdentifier"),
      },
      crm_type: sourceType,
    });
  }

  function onBack() {
    if (isNextStep) {
      resetField("sourceId");
      resetField("mainIdentifier");
      setIsNextStep(false);
    } else {
      goBack();
    }
  }

  let mainComponent = (
    <>
      <SourceTypeFormField control={control} />
      <SourceFormField control={control} />
      {sourceType === "emailbison" && (
        <FormField
          htmlFor="linkedin-url-variable-name"
          label="LinkedIn URL variable name"
          error={errors.mainIdentifier?.message}
        >
          <Input
            variant={isDesktop ? "md" : "lg"}
            placeholder="e.g. linkedin_url"
            id="linkedin-url-variable-name"
            {...register("mainIdentifier")}
          />
        </FormField>
      )}
      <img src={CrmImage} alt="" className="max-lg:order-first" height={238} />
    </>
  );

  const isLoading =
    isLoadingCrmLeadsState || crmLeadsState?.state !== "success";

  if (isNextStep) {
    mainComponent = isLoading ? (
      <div className="flex px-6 py-14 mb-5 flex-col items-center rounded-3xl border border-black-300 bg-black-50">
        <Loader iconSize={32} />
        <p className="text-center text-button-md mt-3 mb-2.5">
          Reading your data
        </p>
        <p className="text-center text-xs-medium text-black-400">
          We're importing your contacts and matching up your columns
        </p>
      </div>
    ) : (
      <CsvOverview
        uploadState={{
          csvState: crmLeadsState.leads,
          sourceName: sourceType,
        }}
        mainIdentifier={mainIdentifier}
        setMainIdentifier={(identifier) =>
          setValue("mainIdentifier", identifier)
        }
      />
    );
  }

  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;

  return (
    <>
      <Header title="Connect CRM" onBack={onBack} />
      <Body className="gap-y-3 lg:gap-y-6">{mainComponent}</Body>
      <Footer className="justify-between">
        <Close asChild>
          <Button variant="secondary-black">Cancel</Button>
        </Close>
        <Button
          disabled={isNextStep ? !mainIdentifier || isLoading : false}
          isLoading={isCreatingSource}
          onClick={isNextStep ? onSave : onNext}
        >
          {isNextStep ? "Add list" : "Next"}
        </Button>
      </Footer>
    </>
  );
}
